import React, { useCallback, useEffect, useRef, useState } from "react";
import { AuthConfig, AuthSession } from "../types";


type AuthGateProps = {
  children: React.ReactNode;
};

type GateStatus = "loading" | "ready" | "signing-in" | "authenticated" | "error";

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleAccountButtonOptions = {
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "large" | "medium" | "small";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  shape?: "rectangular" | "pill" | "circle" | "square";
  width?: number;
  logo_alignment?: "left" | "center";
};

type GoogleIdentityClient = {
  initialize: (options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (parent: HTMLElement, options: GoogleAccountButtonOptions) => void;
  disableAutoSelect?: () => void;
  cancel?: () => void;
};

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleIdentityClient;
        oauth2?: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            include_granted_scopes?: boolean;
            login_hint?: string;
            select_account?: boolean;
            ux_mode?: "popup" | "redirect";
            callback: (response: { code?: string; error?: string; error_description?: string }) => void;
            error_callback?: (error: { type?: string }) => void;
          }) => {
            requestCode: () => void;
          };
        };
      };
    };
  }
}


const GOOGLE_IDENTITY_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
let googleIdentityScriptPromise: Promise<void> | null = null;


function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Google script failed to load")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google script failed to load"));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}


async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { detail?: string };
    if (typeof payload.detail === "string" && payload.detail.trim()) {
      return payload.detail.trim();
    }
  } catch (_error) {
    // Ignore parse failures and fall back to status text.
  }

  return response.statusText || `Request failed with status ${response.status}`;
}


const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<GateStatus>("loading");
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const loadSession = useCallback(async (): Promise<AuthSession> => {
    const response = await fetch("/api/auth/session", { cache: "no-store" });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    return (await response.json()) as AuthSession;
  }, []);

  const loadAuthConfig = useCallback(async (): Promise<AuthConfig> => {
    const response = await fetch("/api/auth/config", { cache: "no-store" });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    return (await response.json()) as AuthConfig;
  }, []);

  const handleGoogleCredential = useCallback(async (credential: string) => {
    setStatus("signing-in");
    setError("");

    try {
      const response = await fetch("/api/auth/google", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ credential }),
      });

      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }

      const nextSession = (await response.json()) as AuthSession;
      setSession(nextSession);
      setStatus("authenticated");
    } catch (authError) {
      console.error("[AIDA] Google sign-in failed:", authError);
      setError(authError instanceof Error ? authError.message : "Google sign-in failed");
      setStatus("ready");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapAuth() {
      try {
        const existingSession = await loadSession();
        if (cancelled) {
          return;
        }

        if (existingSession.authenticated) {
          setSession(existingSession);
          setStatus("authenticated");
          setError("");
          try {
            const config = await loadAuthConfig();
            if (!cancelled) {
              setAuthConfig(config);
            }
          } catch (configError) {
            console.warn("[AIDA] Failed to refresh auth config for active session:", configError);
          }
          return;
        }

        const config = await loadAuthConfig();
        if (cancelled) {
          return;
        }

        setAuthConfig(config);

        if (!config.configured || !config.googleClientId) {
          setStatus("error");
          setError("Google sign-in is not configured on the server.");
          return;
        }

        setStatus("ready");
        setError("");
      } catch (bootstrapError) {
        if (cancelled) {
          return;
        }
        console.error("[AIDA] Failed to bootstrap auth:", bootstrapError);
        setStatus("error");
        setError(bootstrapError instanceof Error ? bootstrapError.message : "Unable to load authentication");
      }
    }

    void bootstrapAuth();

    return () => {
      cancelled = true;
    };
  }, [loadAuthConfig, loadSession]);

  useEffect(() => {
    if (status !== "ready" || !authConfig?.googleClientId || !buttonRef.current) {
      return;
    }

    const clientId = authConfig.googleClientId;
    let cancelled = false;

    loadGoogleIdentityScript()
      .then(() => {
        if (cancelled || !buttonRef.current || !window.google?.accounts?.id) {
          return;
        }

        buttonRef.current.innerHTML = "";
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: GoogleCredentialResponse) => {
            if (!response.credential) {
              setError("Google sign-in did not return a credential.");
              return;
            }
            void handleGoogleCredential(response.credential);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          width: 320,
          logo_alignment: "left",
        });
      })
      .catch((scriptError) => {
        if (cancelled) {
          return;
        }
        console.error("[AIDA] Failed to load Google Identity Services:", scriptError);
        setStatus("error");
        setError("Unable to load Google sign-in.");
      });

    return () => {
      cancelled = true;
      window.google?.accounts?.id?.cancel?.();
    };
  }, [authConfig, handleGoogleCredential, status]);

  const handleLogout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        cache: "no-store",
      });
    } catch (logoutError) {
      console.warn("[AIDA] Logout request failed:", logoutError);
    }

    window.google?.accounts?.id?.disableAutoSelect?.();
    setSession(null);
    setMenuOpen(false);

    if (authConfig?.configured && authConfig.googleClientId) {
      setStatus("ready");
      setError("");
      return;
    }

    setStatus("error");
    setError("Google sign-in is not configured on the server.");
  }, [authConfig]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  if (status === "authenticated" && session?.authenticated) {
    const email = session.user?.email || "authenticated";
    const name = session.user?.name || "Google account";
    const picture = session.user?.picture || "";
    const fallbackInitial = email.charAt(0).toUpperCase() || "A";

    return (
      <div className="auth-shell">
        <div className="auth-profile-control" ref={profileMenuRef}>
          <button
            className={`auth-profile-trigger${menuOpen ? " open" : ""}`}
            type="button"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            {picture ? (
              <img className="auth-avatar" src={picture} alt="" referrerPolicy="no-referrer" />
            ) : (
              <div className="auth-avatar auth-avatar-fallback" aria-hidden="true">
                {fallbackInitial}
              </div>
            )}
          </button>
          <div className={`auth-profile-menu${menuOpen ? " open" : ""}`} role="menu" aria-hidden={!menuOpen}>
            <div className="auth-profile-card">
              <span className="auth-session-label">Signed in</span>
              <span className="auth-session-email" title={email}>
                {name}
              </span>
              <span className="auth-session-detail" title={email}>
                {email}
              </span>
            </div>
            <button className="auth-signout" onClick={handleLogout} type="button" role="menuitem">
              Sign out
            </button>
          </div>
        </div>
        {children}
      </div>
    );
  }

  const statusCopy =
    status === "loading"
      ? "Checking your session..."
      : status === "signing-in"
        ? "Signing you in..."
        : "Sign in with your Google account to use AIDA.";

  return (
    <div className="auth-gate">
      <div className="auth-container">
        <h1 className="auth-title">AIDA</h1>
        <p className="auth-subtitle">Executive Assistant</p>
        <p className="auth-copy">{statusCopy}</p>
        <div className="auth-google-button" ref={buttonRef}>
          {status !== "ready" && <span className="auth-loading-text">Preparing Google sign-in...</span>}
        </div>
        <p className="auth-note">
          Access is limited to Michael McClary&apos;s approved Google account.
        </p>
        {error && <p className="auth-error">{error}</p>}
      </div>
    </div>
  );
};


export default AuthGate;
